import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import SideMenu from '../components/SideMenu';
import RolesManagement from '../components/RolesManagement';
import UsersManagement from '../components/UsersManagement';
import SystemSettingsTab from '../components/SystemSettingsTab';
import ReportAccessTab from '../components/ReportAccessTab';
import LoginLogsTab from '../components/LoginLogsTab';
import TermsConditionsTab from '../components/TermsConditionsTab';
import { Plus, Edit2, Trash2, Menu, ArrowLeft, Save, X, ChevronUp, ChevronDown, Search, Settings as SettingsIcon, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const AdminSettings = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('item-types');
  const [loading, setLoading] = useState(false);

  // Check for tab parameter in URL (works with HashRouter)
  useEffect(() => {
    const hash = window.location.hash;
    const queryIndex = hash.indexOf('?');
    if (queryIndex !== -1) {
      const params = new URLSearchParams(hash.substring(queryIndex));
      const tab = params.get('tab');
      if (tab && tabs.some(t => t.id === tab)) {
        setActiveTab(tab);
      }
    }
  }, []);

  // Check permissions - use effective_role for custom roles with full access
  useEffect(() => {
    const effectiveRole = user.effective_role || user.role;
    if (effectiveRole !== 'Admin' && effectiveRole !== 'Manager') {
      toast.error('Only Admin/Manager can access Settings');
      navigate('/');
    }
  }, [user, navigate]);

  const tabs = [
    { id: 'item-types', label: 'Item Types', icon: '📦' },
    { id: 'purity-gst', label: 'Purity & GST', icon: '⚖️' },
    { id: 'size-presets', label: 'Size Presets', icon: '📏' },
    { id: 'making-presets', label: 'Making Presets', icon: '🔨' },
    { id: 'finish-colors', label: 'Finish/Color', icon: '🎨' },
    { id: 'vendors', label: 'Vendors', icon: '🏢' },
    { id: 'roles', label: 'Roles & Permissions', icon: '🔐' },
    { id: 'users', label: 'User Management', icon: '👥' },
    { id: 'system-settings', label: 'System Settings', icon: '⚙️' },
    { id: 'report-access', label: 'Report Access', icon: '📊' },
    { id: 'login-logs', label: 'Login Logs', icon: '🔒' },
    { id: 'terms-conditions', label: 'Terms & Conditions', icon: '📄' },
    { id: 'quick-links', label: 'Quick Links', icon: '🔗' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <SideMenu user={user} isOpen={menuOpen} onClose={() => setMenuOpen(false)} onLogout={onLogout} />
      
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white shadow-md border-b-2 border-amber-200 px-3 sm:px-4 py-3">
        <div className="flex items-center gap-2 sm:gap-3">
          <button onClick={() => setMenuOpen(true)} className="p-2 hover:bg-amber-100 rounded-xl touch-target" data-testid="menu-button">
            <Menu className="w-5 h-5" />
          </button>
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-amber-100 rounded-xl touch-target">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <SettingsIcon className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600 flex-shrink-0" />
            <h1 className="text-base sm:text-lg font-bold text-gray-800 truncate">Master Data Settings</h1>
          </div>
        </div>
      </div>

      {/* Tab Navigation - Mobile Optimized */}
      <div className="bg-white border-b-2 border-amber-200 sticky top-[60px] z-30">
        {/* Mobile: Dropdown */}
        <div className="block md:hidden px-4 py-3">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            className="w-full px-4 py-2 border-2 border-amber-300 rounded-lg font-semibold text-sm focus:outline-none focus:border-amber-500"
          >
            {tabs.map((tab) => (
              <option key={tab.id} value={tab.id}>
                {tab.icon} {tab.label}
              </option>
            ))}
          </select>
        </div>
        
        {/* Desktop: Horizontal Tabs */}
        <div className="hidden md:block px-4 overflow-x-auto">
          <div className="flex gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 font-semibold text-sm whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'text-amber-600 border-b-4 border-amber-600'
                    : 'text-gray-600 hover:text-amber-600 hover:bg-amber-50'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-4 pb-24">
        {activeTab === 'item-types' && <ItemTypesTab user={user} />}
        {activeTab === 'purity-gst' && <PurityGSTTab user={user} />}
        {activeTab === 'size-presets' && <SizePresetsTab user={user} />}
        {activeTab === 'making-presets' && <MakingPresetsTab user={user} />}
        {activeTab === 'finish-colors' && <FinishColorsTab user={user} />}
        {activeTab === 'vendors' && <VendorsTab user={user} navigate={navigate} />}
        {activeTab === 'roles' && <RolesTab user={user} />}
        {activeTab === 'users' && <UsersTab user={user} />}
        {activeTab === 'system-settings' && <SystemSettingsTab user={user} />}
        {activeTab === 'report-access' && <ReportAccessTab user={user} />}
        {activeTab === 'login-logs' && <LoginLogsTab user={user} />}
        {activeTab === 'terms-conditions' && <TermsConditionsTab user={user} />}
        {activeTab === 'quick-links' && <QuickLinksTab navigate={navigate} />}
      </div>
    </div>
  );
};

// Reusable Master Data Table Component
const MasterDataTable = ({
  title,
  data,
  columns,
  onAdd,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  loading,
  searchPlaceholder = 'Search...',
  addButtonText = 'Add New'
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredData = data.filter((item) =>
    Object.values(item).some((val) =>
      String(val).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <div className="premium-card border-2 border-amber-200">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <h2 className="text-lg font-bold text-gray-800">{title}</h2>
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-initial">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              className="input-field pl-10 w-full sm:w-64"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={onAdd}
            className="btn-primary whitespace-nowrap flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {addButtonText}
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : filteredData.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          {searchTerm ? 'No results found' : 'No data yet. Click "Add New" to get started.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-amber-50 border-b-2 border-amber-200">
                {columns.map((col) => (
                  <th key={col.key} className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    {col.label}
                  </th>
                ))}
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, index) => (
                <tr key={item.id} className="border-b border-gray-200 hover:bg-amber-50">
                  {columns.map((col) => (
                    <td key={col.key} className="px-4 py-3 text-sm">
                      {col.render ? col.render(item[col.key], item) : item[col.key]}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      {onMoveUp && index > 0 && (
                        <button
                          onClick={() => onMoveUp(index)}
                          className="p-2 hover:bg-amber-100 rounded-lg transition-all"
                          title="Move Up"
                        >
                          <ChevronUp className="w-4 h-4 text-gray-600" />
                        </button>
                      )}
                      {onMoveDown && index < filteredData.length - 1 && (
                        <button
                          onClick={() => onMoveDown(index)}
                          className="p-2 hover:bg-amber-100 rounded-lg transition-all"
                          title="Move Down"
                        >
                          <ChevronDown className="w-4 h-4 text-gray-600" />
                        </button>
                      )}
                      <button
                        onClick={() => onEdit(item)}
                        className="p-2 hover:bg-blue-100 rounded-lg transition-all"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4 text-blue-600" />
                      </button>
                      <button
                        onClick={() => onDelete(item)}
                        className="p-2 hover:bg-red-100 rounded-lg transition-all"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Item Types Tab
const ItemTypesTab = ({ user }) => {
  const [itemTypes, setItemTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', is_active: true });

  useEffect(() => {
    loadItemTypes();
  }, []);

  const loadItemTypes = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/masters/item-types');
      setItemTypes(res.data);
    } catch (err) {
      console.error('Error loading item types:', err);
      toast.error('Failed to load item types');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({ name: '', is_active: true });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ name: item.name, is_active: item.is_active });
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;

    try {
      await axiosInstance.delete(`/masters/item-types/${item.id}`);
      toast.success('Item type deleted successfully');
      loadItemTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete item type');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      if (editingItem) {
        await axiosInstance.put(`/masters/item-types/${editingItem.id}`, formData);
        toast.success('Item type updated successfully');
      } else {
        await axiosInstance.post('/masters/item-types', formData);
        toast.success('Item type created successfully');
      }
      setShowModal(false);
      loadItemTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save item type');
    }
  };

  const handleMoveUp = async (index) => {
    const newData = [...itemTypes];
    [newData[index], newData[index - 1]] = [newData[index - 1], newData[index]];
    const reorderData = newData.map((item, idx) => ({ id: item.id, display_order: idx + 1 }));
    
    try {
      await axiosInstance.post('/masters/item-types/reorder', reorderData);
      loadItemTypes();
    } catch (err) {
      toast.error('Failed to reorder');
    }
  };

  const handleMoveDown = async (index) => {
    handleMoveUp(index + 1);
  };

  const columns = [
    { key: 'name', label: 'Item Type Name' },
    { key: 'display_order', label: 'Order' },
    {
      key: 'is_active',
      label: 'Status',
      render: (val) => (
        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${val ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
          {val ? '✅ Active' : '❌ Inactive'}
        </span>
      ),
    },
  ];

  return (
    <>
      <MasterDataTable
        title="Item Types"
        data={itemTypes}
        columns={columns}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onMoveUp={handleMoveUp}
        onMoveDown={handleMoveDown}
        loading={loading}
        searchPlaceholder="Search item types..."
        addButtonText="Add Item Type"
      />

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                {editingItem ? 'Edit Item Type' : 'Add Item Type'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  className="input-field w-full"
                  placeholder="e.g., Ring, Chain, Necklace"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  autoFocus
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_active" className="text-sm font-semibold text-gray-700">
                  Active
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn-primary">
                  <Save className="w-4 h-4 mr-2" />
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

// Purity & GST Tab
const PurityGSTTab = ({ user }) => {
  const [purityOptions, setPurityOptions] = useState([]);
  const [gstPercent, setGstPercent] = useState(3.0);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', multiplier: 1.0, is_active: true });
  const [editingGST, setEditingGST] = useState(false);
  const [tempGST, setTempGST] = useState(3.0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [purityRes, gstRes] = await Promise.all([
        axiosInstance.get('/masters/purity-options'),
        axiosInstance.get('/masters/gst-config'),
      ]);
      setPurityOptions(purityRes.data);
      setGstPercent(gstRes.data.gst_percent || 3.0);
      setTempGST(gstRes.data.gst_percent || 3.0);
    } catch (err) {
      console.error('Error loading data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGST = async () => {
    try {
      await axiosInstance.post('/masters/gst-config', { gst_percent: tempGST });
      setGstPercent(tempGST);
      setEditingGST(false);
      toast.success('GST percentage updated successfully');
    } catch (err) {
      toast.error('Failed to update GST percentage');
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({ name: '', multiplier: 1.0, is_active: true });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ name: item.name, multiplier: item.multiplier, is_active: item.is_active });
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;

    try {
      await axiosInstance.delete(`/masters/purity-options/${item.id}`);
      toast.success('Purity option deleted successfully');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (formData.multiplier < 0.1 || formData.multiplier > 1.0) {
      toast.error('Multiplier must be between 0.1 and 1.0');
      return;
    }

    try {
      if (editingItem) {
        await axiosInstance.put(`/masters/purity-options/${editingItem.id}`, formData);
        toast.success('Purity option updated successfully');
      } else {
        await axiosInstance.post('/masters/purity-options', formData);
        toast.success('Purity option created successfully');
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleMoveUp = async (index) => {
    const newData = [...purityOptions];
    [newData[index], newData[index - 1]] = [newData[index - 1], newData[index]];
    const reorderData = newData.map((item, idx) => ({ id: item.id, display_order: idx + 1 }));
    
    try {
      await axiosInstance.post('/masters/purity-options/reorder', reorderData);
      loadData();
    } catch (err) {
      toast.error('Failed to reorder');
    }
  };

  const handleMoveDown = async (index) => {
    handleMoveUp(index + 1);
  };

  const columns = [
    { key: 'name', label: 'Purity' },
    { 
      key: 'multiplier', 
      label: 'Multiplier',
      render: (val) => <span className="font-mono font-semibold">{val.toFixed(3)}</span>
    },
    { key: 'display_order', label: 'Order' },
    {
      key: 'is_active',
      label: 'Status',
      render: (val) => (
        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${val ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
          {val ? '✅ Active' : '❌ Inactive'}
        </span>
      ),
    },
  ];

  return (
    <>
      {/* GST Configuration Card */}
      <div className="premium-card border-2 border-blue-200 bg-blue-50 mb-4">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-bold text-gray-800">GST Configuration</h3>
          {!editingGST && (
            <button
              onClick={() => setEditingGST(true)}
              className="btn-primary text-sm"
            >
              <Edit2 className="w-4 h-4 mr-1" />
              Edit
            </button>
          )}
        </div>

        {editingGST ? (
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-1">GST Percentage</label>
              <input
                type="number"
                step="0.1"
                className="input-field w-full"
                value={tempGST}
                onChange={(e) => setTempGST(parseFloat(e.target.value))}
              />
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={handleSaveGST} className="btn-primary">
                <Save className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setEditingGST(false);
                  setTempGST(gstPercent);
                }}
                className="px-3 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="text-3xl font-bold text-blue-600">{gstPercent}%</div>
        )}
      </div>

      {/* Purity Options Table */}
      <MasterDataTable
        title="Purity Options"
        data={purityOptions}
        columns={columns}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onMoveUp={handleMoveUp}
        onMoveDown={handleMoveDown}
        loading={loading}
        searchPlaceholder="Search purity options..."
        addButtonText="Add Purity"
      />

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                {editingItem ? 'Edit Purity Option' : 'Add Purity Option'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  className="input-field w-full"
                  placeholder="e.g., 24K, 22K, 18K"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Multiplier * (0.1 - 1.0)
                </label>
                <input
                  type="number"
                  step="0.001"
                  min="0.1"
                  max="1.0"
                  className="input-field w-full"
                  placeholder="e.g., 1.0, 0.916, 0.76"
                  value={formData.multiplier}
                  onChange={(e) => setFormData({ ...formData, multiplier: parseFloat(e.target.value) })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Example: 24K=1.0, 22K=0.916, 18K=0.76
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active_purity"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_active_purity" className="text-sm font-semibold text-gray-700">
                  Active
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn-primary">
                  <Save className="w-4 h-4 mr-2" />
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

// Size Presets Tab
const SizePresetsTab = ({ user }) => {
  const [sizePresets, setSizePresets] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    preset_name: '',
    item_types: [],
    sizes: [],
    is_active: true
  });
  const [sizeInput, setSizeInput] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [presetsRes, typesRes] = await Promise.all([
        axiosInstance.get('/masters/size-presets'),
        axiosInstance.get('/masters/item-types'),
      ]);
      setSizePresets(presetsRes.data);
      setItemTypes(typesRes.data.filter(t => t.is_active));
    } catch (err) {
      console.error('Error loading data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({ preset_name: '', item_types: [], sizes: [], is_active: true });
    setSizeInput('');
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    // Handle both old (item_type + presets) and new (preset_name + item_types + sizes) structures
    const itemTypesArray = item.item_types || (item.item_type ? [item.item_type] : []);
    const sizesArray = item.sizes || item.presets || [];
    
    setFormData({
      preset_name: item.preset_name || item.item_type || '',
      item_types: itemTypesArray,
      sizes: sizesArray,
      is_active: item.is_active !== false
    });
    setSizeInput('');
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    const itemName = item.preset_name || item.item_type || 'this preset';
    if (!window.confirm(`Delete "${itemName}"?`)) return;

    try {
      await axiosInstance.delete(`/masters/size-presets/${item.id}`);
      toast.success('Size preset deleted successfully');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.preset_name.trim()) {
      toast.error('Preset name is required');
      return;
    }
    if (formData.item_types.length === 0) {
      toast.error('Please select at least one item type');
      return;
    }
    if (formData.sizes.length === 0) {
      toast.error('Please add at least one size');
      return;
    }

    try {
      if (editingItem) {
        await axiosInstance.put(`/masters/size-presets/${editingItem.id}`, formData);
        toast.success('Size preset updated successfully');
      } else {
        await axiosInstance.post('/masters/size-presets', formData);
        toast.success('Size preset created successfully');
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const toggleItemType = (itemType) => {
    const current = formData.item_types || [];
    if (current.includes(itemType)) {
      setFormData({ ...formData, item_types: current.filter(t => t !== itemType) });
    } else {
      setFormData({ ...formData, item_types: [...current, itemType] });
    }
  };

  const addSize = () => {
    if (!sizeInput.trim()) return;
    if (formData.sizes.includes(sizeInput.trim())) {
      toast.error('Size already exists');
      return;
    }
    setFormData({ ...formData, sizes: [...formData.sizes, sizeInput.trim()] });
    setSizeInput('');
  };

  const removeSize = (size) => {
    setFormData({ ...formData, sizes: formData.sizes.filter(s => s !== size) });
  };

  const handleMoveUp = async (index) => {
    const newData = [...sizePresets];
    [newData[index], newData[index - 1]] = [newData[index - 1], newData[index]];
    const reorderData = newData.map((item, idx) => ({ id: item.id, display_order: idx + 1 }));
    
    try {
      await axiosInstance.post('/masters/size-presets/reorder', reorderData);
      loadData();
    } catch (err) {
      toast.error('Failed to reorder');
    }
  };

  const handleMoveDown = async (index) => {
    handleMoveUp(index + 1);
  };

  const columns = [
    { 
      key: 'preset_name', 
      label: 'Preset Name',
      render: (val, item) => val || item.item_type || '-'  // Handle old structure
    },
    {
      key: 'item_types',
      label: 'Item Types',
      render: (val, item) => {
        // Handle both old (item_type string) and new (item_types array) structures
        const types = val || (item.item_type ? [item.item_type] : []);
        return (
          <div className="flex flex-wrap gap-1">
            {types.map((type, idx) => (
              <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                {type}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: 'sizes',
      label: 'Sizes',
      render: (val, item) => {
        // Handle both old (presets array) and new (sizes array) structures
        const sizeArray = val || item.presets || [];
        return (
          <div className="text-xs text-gray-600">
            {sizeArray.slice(0, 5).join(', ')}
            {sizeArray.length > 5 && ` +${sizeArray.length - 5} more`}
          </div>
        );
      },
    },
    { 
      key: 'display_order', 
      label: 'Order',
      render: (val) => val || '-'
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (val) => (
        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${val !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
          {val !== false ? '✅ Active' : '❌ Inactive'}
        </span>
      ),
    },
  ];

  return (
    <>
      <MasterDataTable
        title="Size Presets"
        data={sizePresets}
        columns={columns}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onMoveUp={handleMoveUp}
        onMoveDown={handleMoveDown}
        loading={loading}
        searchPlaceholder="Search size presets..."
        addButtonText="Add Size Preset"
      />

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 my-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                {editingItem ? 'Edit Size Preset' : 'Add Size Preset'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Preset Name *</label>
                <input
                  type="text"
                  className="input-field w-full"
                  placeholder="e.g., Standard Ring Sizes, Bangle Sizes"
                  value={formData.preset_name}
                  onChange={(e) => setFormData({ ...formData, preset_name: e.target.value })}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Item Types * (Select multiple)
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {itemTypes.map((type) => (
                    <label key={type.id} className="flex items-center gap-2 cursor-pointer hover:bg-amber-50 p-2 rounded">
                      <input
                        type="checkbox"
                        checked={formData.item_types.includes(type.name)}
                        onChange={() => toggleItemType(type.name)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{type.name}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Select all item types that can use this size preset
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Sizes *</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    className="input-field flex-1"
                    placeholder='e.g., "6", "2.4", "18 inch"'
                    value={sizeInput}
                    onChange={(e) => setSizeInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSize())}
                  />
                  <button
                    type="button"
                    onClick={addSize}
                    className="px-4 py-2 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600"
                  >
                    Add
                  </button>
                </div>
                {formData.sizes.length > 0 && (
                  <div className="flex flex-wrap gap-2 border border-gray-200 rounded-lg p-3">
                    {formData.sizes.map((size, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold flex items-center gap-2"
                      >
                        {size}
                        <button
                          type="button"
                          onClick={() => removeSize(size)}
                          className="hover:bg-blue-200 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active_size"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_active_size" className="text-sm font-semibold text-gray-700">
                  Active
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn-primary">
                  <Save className="w-4 h-4 mr-2" />
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

// Making Presets Tab - Purity-Based with Calculation Type
const MakingPresetsTab = ({ user }) => {
  const [makingPresets, setMakingPresets] = useState([]);
  const [purityOptions, setPurityOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ purity: '', calculation_type: 'percentage', presets: [] });
  const [presetInput, setPresetInput] = useState('');

  const calculationTypes = [
    { value: 'percentage', label: 'Percentage-wise (%)' },
    { value: 'per_gram', label: 'Per Gram (₹/gram)' },
    { value: 'flat_rate', label: 'Flat Rate (₹)' },
    { value: 'wastage', label: 'Wastage %Wst (T)' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [presetsRes, purityRes] = await Promise.all([
        axiosInstance.get('/masters/making-presets'),
        axiosInstance.get('/masters/purity-options'),
      ]);
      setMakingPresets(presetsRes.data);
      setPurityOptions(purityRes.data.filter(p => p.is_active !== false));
    } catch (err) {
      console.error('Error loading data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({ purity: '', calculation_type: 'percentage', presets: [] });
    setPresetInput('');
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      purity: item.purity || '',
      calculation_type: item.calculation_type || 'percentage',
      presets: item.presets || []
    });
    setPresetInput('');
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    const itemName = item.purity || item.name || 'this preset';
    const calcType = item.calculation_type ? ` (${calculationTypes.find(t => t.value === item.calculation_type)?.label || item.calculation_type})` : '';
    if (!window.confirm(`Delete making presets for "${itemName}${calcType}"?`)) return;

    try {
      await axiosInstance.delete(`/masters/making-presets/${item.id}`);
      toast.success('Making preset deleted successfully');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.purity.trim()) {
      toast.error('Please select a purity');
      return;
    }
    if (!formData.calculation_type) {
      toast.error('Please select a calculation type');
      return;
    }
    if (formData.presets.length === 0) {
      toast.error('Please add at least one preset value');
      return;
    }

    try {
      const payload = {
        id: editingItem ? editingItem.id : `making-${formData.purity.toLowerCase().replace(/[^a-z0-9]/g, '')}-${formData.calculation_type}`,
        purity: formData.purity,
        calculation_type: formData.calculation_type,
        presets: formData.presets
      };

      if (editingItem) {
        await axiosInstance.put(`/masters/making-presets/${editingItem.id}`, payload);
        toast.success('Making preset updated successfully');
      } else {
        await axiosInstance.post('/masters/making-presets', payload);
        toast.success('Making preset created successfully');
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const addPreset = () => {
    if (!presetInput.trim()) return;
    if (formData.presets.includes(presetInput.trim())) {
      toast.error('Preset value already exists');
      return;
    }
    setFormData({ ...formData, presets: [...formData.presets, presetInput.trim()] });
    setPresetInput('');
  };

  const removePreset = (preset) => {
    setFormData({ ...formData, presets: formData.presets.filter(p => p !== preset) });
  };

  const handleMoveUp = async (index) => {
    const newData = [...makingPresets];
    [newData[index], newData[index - 1]] = [newData[index - 1], newData[index]];
    const reorderData = newData.map((item, idx) => ({ id: item.id, display_order: idx + 1 }));
    
    try {
      await axiosInstance.post('/masters/making-presets/reorder', reorderData);
      loadData();
    } catch (err) {
      toast.error('Failed to reorder');
    }
  };

  const handleMoveDown = async (index) => {
    handleMoveUp(index + 1);
  };

  const getCalculationTypeLabel = (type) => {
    const found = calculationTypes.find(t => t.value === type);
    return found ? found.label : type || '-';
  };

  const columns = [
    { 
      key: 'purity', 
      label: 'Purity',
      render: (val, item) => {
        if (val) return <span className="font-semibold text-blue-700">{val}</span>;
        if (item.name) return <span className="text-gray-600">{item.name}</span>;
        return '-';
      }
    },
    {
      key: 'calculation_type',
      label: 'Calculation Type',
      render: (val) => (
        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-semibold">
          {getCalculationTypeLabel(val)}
        </span>
      )
    },
    {
      key: 'presets',
      label: 'Preset Values',
      render: (val) => {
        if (!val || val.length === 0) return <span className="text-gray-400">No presets</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {val.slice(0, 4).map((preset, idx) => (
              <span key={idx} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                {preset}
              </span>
            ))}
            {val.length > 4 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-semibold">
                +{val.length - 4} more
              </span>
            )}
          </div>
        );
      }
    },
    { 
      key: 'display_order', 
      label: 'Order',
      render: (val) => val || '-'
    },
  ];

  return (
    <>
      <div className="premium-card border-2 border-blue-200 bg-blue-50 mb-4 p-4">
        <div className="flex items-start gap-3">
          <div className="text-2xl">ℹ️</div>
          <div>
            <h4 className="font-bold text-gray-800 mb-1">About Making Presets</h4>
            <p className="text-sm text-gray-700 mb-2">
              Making presets are organized by <strong>Purity</strong> and <strong>Calculation Type</strong>. Create different presets for each combination.
            </p>
            <div className="text-sm text-gray-700 space-y-1">
              <p><strong>Examples:</strong></p>
              <p>• 22K + Percentage → [10.9%, 11.9%, 12.9%]</p>
              <p>• 22K + Per Gram → [350, 450, 550]</p>
              <p>• 22K + Flat Rate → [5000, 6000, 7000]</p>
              <p>• 22K + Wastage %Wst → [88, 90, 92]</p>
            </div>
          </div>
        </div>
      </div>

      <MasterDataTable
        title="Making Presets (Purity-Based with Calculation Type)"
        data={makingPresets}
        columns={columns}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onMoveUp={handleMoveUp}
        onMoveDown={handleMoveDown}
        loading={loading}
        searchPlaceholder="Search making presets..."
        addButtonText="Add Making Preset"
      />

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 my-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                {editingItem ? 'Edit Making Preset' : 'Add Making Preset'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Purity *
                  </label>
                  <select
                    className="input-field w-full"
                    value={formData.purity}
                    onChange={(e) => setFormData({ ...formData, purity: e.target.value })}
                    disabled={editingItem !== null}
                  >
                    <option value="">Select Purity</option>
                    {purityOptions.map((purity) => (
                      <option key={purity.id} value={purity.name}>
                        {purity.name}
                      </option>
                    ))}
                  </select>
                  {editingItem && (
                    <p className="text-xs text-gray-500 mt-1">
                      Cannot change when editing
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Calculation Type *
                  </label>
                  <select
                    className="input-field w-full"
                    value={formData.calculation_type}
                    onChange={(e) => setFormData({ ...formData, calculation_type: e.target.value })}
                    disabled={editingItem !== null}
                  >
                    {calculationTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {editingItem && (
                    <p className="text-xs text-gray-500 mt-1">
                      Cannot change when editing
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Preset Values *
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    className="input-field flex-1"
                    placeholder={
                      formData.calculation_type === 'percentage' ? 'e.g., "10.9", "11.9"' :
                      formData.calculation_type === 'per_gram' ? 'e.g., "350", "450"' :
                      formData.calculation_type === 'wastage' ? 'e.g., "88", "92"' :
                      'e.g., "5000", "6000"'
                    }
                    value={presetInput}
                    onChange={(e) => setPresetInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPreset())}
                  />
                  <button
                    type="button"
                    onClick={addPreset}
                    className="px-4 py-2 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600"
                  >
                    Add
                  </button>
                </div>
                {formData.presets.length > 0 && (
                  <div className="flex flex-wrap gap-2 border border-gray-200 rounded-lg p-3">
                    {formData.presets.map((preset, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold flex items-center gap-2"
                      >
                        {preset}
                        {formData.calculation_type === 'percentage' && '%'}
                        {formData.calculation_type === 'per_gram' && '/g'}
                        {formData.calculation_type === 'flat_rate' && '/-'}
                        {formData.calculation_type === 'wastage' && ' %Wst'}
                        <button
                          type="button"
                          onClick={() => removePreset(preset)}
                          className="hover:bg-green-200 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {formData.calculation_type === 'percentage' && 'Enter percentage values (e.g., 10.9, 11.9, 12.9)'}
                  {formData.calculation_type === 'per_gram' && 'Enter per gram amounts (e.g., 350, 450, 550)'}
                  {formData.calculation_type === 'flat_rate' && 'Enter flat rate amounts (e.g., 5000, 6000, 7000)'}
                  {formData.calculation_type === 'wastage' && 'Enter wastage values (e.g., 88, 90, 92)'}
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn-primary">
                  <Save className="w-4 h-4 mr-2" />
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

// Finish/Colors Tab
const FinishColorsTab = ({ user }) => {
  const [finishColors, setFinishColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', is_active: true });

  useEffect(() => {
    loadFinishColors();
  }, []);

  const loadFinishColors = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/masters/finish-colors');
      setFinishColors(res.data);
    } catch (err) {
      console.error('Error loading finish colors:', err);
      toast.error('Failed to load finish colors');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({ name: '', is_active: true });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ name: item.name, is_active: item.is_active });
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;

    try {
      await axiosInstance.delete(`/masters/finish-colors/${item.id}`);
      toast.success('Finish color deleted successfully');
      loadFinishColors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      if (editingItem) {
        await axiosInstance.put(`/masters/finish-colors/${editingItem.id}`, formData);
        toast.success('Finish color updated successfully');
      } else {
        await axiosInstance.post('/masters/finish-colors', formData);
        toast.success('Finish color created successfully');
      }
      setShowModal(false);
      loadFinishColors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleMoveUp = async (index) => {
    const newData = [...finishColors];
    [newData[index], newData[index - 1]] = [newData[index - 1], newData[index]];
    const reorderData = newData.map((item, idx) => ({ id: item.id, display_order: idx + 1 }));
    
    try {
      await axiosInstance.post('/masters/finish-colors/reorder', reorderData);
      loadFinishColors();
    } catch (err) {
      toast.error('Failed to reorder');
    }
  };

  const handleMoveDown = async (index) => {
    handleMoveUp(index + 1);
  };

  const columns = [
    { key: 'name', label: 'Finish/Color Name' },
    { key: 'display_order', label: 'Order' },
    {
      key: 'is_active',
      label: 'Status',
      render: (val) => (
        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${val ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
          {val ? '✅ Active' : '❌ Inactive'}
        </span>
      ),
    },
  ];

  return (
    <>
      <MasterDataTable
        title="Finish/Color Options"
        data={finishColors}
        columns={columns}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onMoveUp={handleMoveUp}
        onMoveDown={handleMoveDown}
        loading={loading}
        searchPlaceholder="Search finish/colors..."
        addButtonText="Add Finish/Color"
      />

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                {editingItem ? 'Edit Finish/Color' : 'Add Finish/Color'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  className="input-field w-full"
                  placeholder="e.g., Yellow Gold, Rose Gold, White Gold"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  autoFocus
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active_finish"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_active_finish" className="text-sm font-semibold text-gray-700">
                  Active
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn-primary">
                  <Save className="w-4 h-4 mr-2" />
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

// ===================== VENDORS TAB =====================
const VendorsTab = ({ user, navigate }) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    short_code: '',
    mobile: '',
    specialization: '',
    is_active: true
  });

  useEffect(() => {
    loadVendors();
  }, []);

  const loadVendors = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/vendors');
      setVendors(res.data);
    } catch (err) {
      console.error('Error loading vendors:', err);
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({ name: '', short_code: '', mobile: '', specialization: '', is_active: true });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      short_code: item.short_code || '',
      mobile: item.mobile || '',
      specialization: item.specialization || '',
      is_active: item.is_active !== false
    });
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete vendor "${item.name}"?`)) return;

    try {
      await axiosInstance.delete(`/vendors/${item.id}`);
      toast.success('Vendor deleted successfully');
      loadVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete vendor');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!formData.short_code.trim()) {
      toast.error('Short code is required');
      return;
    }

    try {
      if (editingItem) {
        await axiosInstance.put(`/vendors/${editingItem.id}`, formData);
        toast.success('Vendor updated successfully');
      } else {
        await axiosInstance.post('/vendors', formData);
        toast.success('Vendor created successfully');
      }
      setShowModal(false);
      loadVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save vendor');
    }
  };

  const columns = [
    { key: 'name', label: 'Vendor Name' },
    { key: 'short_code', label: 'Short Code' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'specialization', label: 'Specialization' },
    {
      key: 'is_active',
      label: 'Status',
      render: (val) => (
        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${val !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
          {val !== false ? '✅ Active' : '❌ Inactive'}
        </span>
      ),
    },
  ];

  return (
    <>
      <MasterDataTable
        title="Vendors Management"
        data={vendors}
        columns={columns}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        loading={loading}
        searchPlaceholder="Search vendors..."
        addButtonText="Add Vendor"
      />

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                {editingItem ? 'Edit Vendor' : 'Add Vendor'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Vendor Name *</label>
                <input
                  type="text"
                  className="input-field w-full"
                  placeholder="e.g., ABC Jewelers"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Short Code *</label>
                <input
                  type="text"
                  className="input-field w-full"
                  placeholder="e.g., ABC"
                  value={formData.short_code}
                  onChange={(e) => setFormData({ ...formData, short_code: e.target.value.toUpperCase() })}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Mobile</label>
                <input
                  type="tel"
                  className="input-field w-full"
                  placeholder="e.g., 9876543210"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Specialization</label>
                <input
                  type="text"
                  className="input-field w-full"
                  placeholder="e.g., Gold Jewelry, Diamond Setting"
                  value={formData.specialization}
                  onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active_vendor"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_active_vendor" className="text-sm font-semibold text-gray-700">
                  Active
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn-primary">
                  <Save className="w-4 h-4 mr-2" />
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

// ===================== ROLES TAB =====================
const RolesTab = ({ user }) => {
  return <RolesManagement user={user} />;
};

// ===================== USERS TAB =====================
const UsersTab = ({ user }) => {
  return <UsersManagement user={user} />;
};

// ===================== QUICK LINKS TAB =====================
const QuickLinksTab = ({ navigate }) => {
  const quickLinks = [
    { label: 'Vendors Management', path: '/vendors', icon: '📦', description: 'Manage vendor codes and details' },
    { label: 'Booking Types', path: '/admin/booking-types', icon: '📋', description: 'Manage booking type categories' },
    { label: 'Signature Names', path: '/admin/signature-names', icon: '✍️', description: 'Manage authorized signature names' },
    { label: 'Customers', path: '/customers', icon: '👥', description: 'View and manage customers' },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {quickLinks.map((link) => (
        <div
          key={link.path}
          onClick={() => navigate(link.path)}
          className="premium-card border-2 border-amber-200 hover:border-amber-400 cursor-pointer transition-all hover:shadow-lg"
        >
          <div className="flex items-start gap-4">
            <div className="text-4xl">{link.icon}</div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
                {link.label}
                <ExternalLink className="w-4 h-4 text-gray-400" />
              </h3>
              <p className="text-sm text-gray-600">{link.description}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdminSettings;
